import { useEffect, useState, type FormEvent } from 'react';
import {
  fetchCategories,
  createCategory,
  updateCategory,
  deleteCategory,
  type Category,
} from '@/services/products';
import Modal, { inputCls, labelCls } from '@/components/base/Modal';

interface CategoriesModalProps {
  onClose: () => void;
  onChanged: () => void;
}

export default function CategoriesModal({ onClose, onChanged }: CategoriesModalProps) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    load();
  }, []);

  async function load() {
    const res = await fetchCategories();
    setCategories(res.data);
  }

  function resetForm() {
    setName('');
    setDescription('');
    setEditingId(null);
    setError('');
  }

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) {
      setError('El nombre es obligatorio.');
      return;
    }
    setBusy(true);
    setError('');
    const res = editingId
      ? await updateCategory(editingId, { name, description })
      : await createCategory({ name, description });
    setBusy(false);
    if (res.error) {
      setError(res.error);
      return;
    }
    resetForm();
    await load();
    onChanged();
  }

  function startEdit(c: Category) {
    setEditingId(c.id);
    setName(c.name);
    setDescription(c.description);
    setConfirmDeleteId(null);
    setError('');
  }

  async function handleDelete(c: Category) {
    setBusy(true);
    setError('');
    const res = await deleteCategory(c.id);
    setBusy(false);
    if (res.error) {
      setError(res.error);
      setConfirmDeleteId(null);
      return;
    }
    setConfirmDeleteId(null);
    if (editingId === c.id) resetForm();
    await load();
    onChanged();
  }

  return (
    <Modal
      title="Categorías"
      subtitle="Crea, edita y elimina las categorías del catálogo."
      onClose={onClose}
      maxWidth="max-w-2xl"
    >
      <div className="space-y-4">
        {error && (
          <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2.5">
            <i className="ri-error-warning-line"></i>
            <span>{error}</span>
          </div>
        )}

        <form
          onSubmit={handleSave}
          className="border border-background-200 rounded-lg p-4 space-y-3 bg-background-50"
        >
          <p className="text-sm font-semibold text-foreground-900">
            {editingId ? 'Editar categoría' : 'Nueva categoría'}
          </p>
          <div>
            <label className={labelCls}>Nombre *</label>
            <input
              className={inputCls}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ej. Perros Grandes"
            />
          </div>
          <div>
            <label className={labelCls}>Descripción</label>
            <input
              className={inputCls}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Opcional"
            />
          </div>
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={busy}
              className="px-4 py-2 text-sm font-semibold bg-accent-500 text-white rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60"
            >
              {editingId ? 'Guardar cambios' : 'Agregar categoría'}
            </button>
            {editingId && (
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 text-sm border border-background-300 text-foreground-600 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                Cancelar
              </button>
            )}
          </div>
        </form>

        <div className="space-y-2">
          {categories.length === 0 && (
            <p className="text-sm text-foreground-500 text-center py-6">No hay categorías todavía.</p>
          )}
          {categories.map((c) => (
            <div
              key={c.id}
              className="flex items-center gap-3 border border-background-200 rounded-lg px-4 py-3 bg-white"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground-900">{c.name}</p>
                <p className="text-xs text-foreground-500 truncate">
                  {c.description || 'Sin descripción'}
                </p>
              </div>
              {confirmDeleteId === c.id ? (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-foreground-600">¿Eliminar?</span>
                  <button
                    onClick={() => handleDelete(c)}
                    disabled={busy}
                    className="px-3 py-1.5 text-xs font-semibold bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Sí
                  </button>
                  <button
                    onClick={() => setConfirmDeleteId(null)}
                    className="px-3 py-1.5 text-xs border border-background-300 text-foreground-600 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    No
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => startEdit(c)}
                    title="Editar"
                    className="w-8 h-8 flex items-center justify-center rounded-md text-accent-600 hover:bg-accent-100 transition-colors cursor-pointer"
                  >
                    <i className="ri-edit-box-line"></i>
                  </button>
                  <button
                    onClick={() => setConfirmDeleteId(c.id)}
                    title="Eliminar"
                    className="w-8 h-8 flex items-center justify-center rounded-md text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                  >
                    <i className="ri-delete-bin-line"></i>
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
}